/**
 * Session Abort Controller
 *
 * Manages a global AbortController and a monotonically increasing token
 * generation counter. Every authenticated HTTP request attaches the current
 * session signal so it can be cancelled instantly when a session switch
 * occurs. The generation counter lets the 401 handler distinguish stale-token
 * errors from genuine auth failures.
 *
 * Usage:
 * - Interceptor methods call `getSessionSignal()` to attach the abort signal.
 * - Interceptor methods stamp `_tokenGeneration: getTokenGeneration()` onto
 *   the axios config so the 401 handler can compare generations.
 * - `applyAuthSession()` / `clearStoredAuth()` call `abortAllPendingRequests()`
 *   before modifying tokens or Redux state.
 */

let controller = new AbortController();
let tokenGeneration = 0;
const abortHandlers = new Set<() => void>();

/**
 * Returns the current session's AbortSignal.
 * Attach this to every authenticated axios request as `signal`.
 */
export const getSessionSignal = (): AbortSignal => controller.signal;

/**
 * Returns the current token generation number.
 * Stamp this onto the axios config so the 401 handler can detect stale requests.
 */
export const getTokenGeneration = (): number => tokenGeneration;

/**
 * Registers a callback to run whenever abortAllPendingRequests() is called.
 * Returns an unregister function.
 */
export const registerAbortHandler = (handler: () => void): (() => void) => {
  abortHandlers.add(handler);
  return () => {
    abortHandlers.delete(handler);
  };
};

/**
 * Aborts all in-flight requests belonging to the current session and
 * prepares a fresh AbortController for the next session.
 *
 * Call this **before** writing a new token to localStorage or clearing
 * Redux state, so that no stale response can race into the 401 handler
 * and destroy the new session.
 */
export const abortAllPendingRequests = (): void => {
  controller.abort();
  abortHandlers.forEach((handler) => {
    try {
      handler();
    } catch {
      // Best effort
    }
  });
  controller = new AbortController();
  tokenGeneration += 1;
};
